import { AsyncPipe, I18nPluralPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
    CheckboxComponent,
    CheckboxGroupComponent,
    DialogButtonComponent,
    DialogButtonsComponent,
    DialogComponent,
    LoaderComponent,
    RadioButtonComponent,
    RadioGroupComponent,
    SeparatorComponent,
    ToggleButtonComponent,
} from '@interacta-shared/ui';
import { CommunityType } from '@interacta-shared/util-caronte';
import { CommunitiesStateService } from '@interacta/data-access-community-tree';
import { ICommunity } from '@interacta/util-community';
import { dashboardCommunity } from '@modules/app-routing/routes';
import { changeFilters } from '@modules/communities/store/post/post.actions';
import { HashtagInfo } from '@modules/hashtag-info/models/hashtag-info.model';
import {
    closeDetail,
    followHashtag,
} from '@modules/hashtag-info/store/hashtag-info.actions';
import {
    selectDetailHashtagInfo,
    selectDetailVisible,
} from '@modules/hashtag-info/store/hashtag-info.selectors';
import { PostPreviewService } from '@modules/post-preview/services/post-preview.service';
import { emptyPostFilters } from '@modules/post/models/filter-post/filter-post.utils';
import { DashboardView } from '@modules/post/models/post-list.model';
import { UIStateService } from '@modules/state/services/ui-state.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

export type SearchType = 'post' | 'attachment' | 'both';

@Component({
    selector: 'interacta-hashtag-info-dialog',
    templateUrl: './hashtag-info-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoaderComponent,
        DialogComponent,
        AsyncPipe,
        TranslatePipe,
        I18nPluralPipe,
        ToggleButtonComponent,
        SeparatorComponent,
        RadioGroupComponent,
        RadioButtonComponent,
        CheckboxComponent,
        CheckboxGroupComponent,
        ReactiveFormsModule,
        DialogButtonsComponent,
        DialogButtonComponent,
    ],
})
export class HashtagInfoDialogComponent {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly communitiesStateService = inject(CommunitiesStateService);
    private readonly uiStateService = inject(UIStateService);
    private readonly postPreviewService = inject(PostPreviewService);

    visible = this.store.selectSignal(selectDetailVisible);
    hashtag = this.store.selectSignal(selectDetailHashtagInfo);
    community$: Observable<ICommunity | undefined>;
    searchType = new FormControl<SearchType[]>([]);

    constructor() {
        this.community$ = toObservable(this.hashtag).pipe(
            mergeMap((hashtag) =>
                hashtag
                    ? this.communitiesStateService.getCommunity(
                          hashtag.communityId,
                      )
                    : of(undefined),
            ),
        );
    }

    close(): void {
        this.store.dispatch(closeDetail());
        this.searchType.reset();
    }

    toggleFollow(hashtag: HashtagInfo): void {
        this.store.dispatch(
            followHashtag({
                hashtagId: hashtag.id,
                follow: !hashtag.followedByMe,
            }),
        );
    }

    apply(
        hashtag: HashtagInfo,
        searchTypes: SearchType[] | null,
        community?: Pick<ICommunity, 'type'> | null,
    ): void {
        if (searchTypes?.length) {
            /* se l'anteprima del post è aperta, la chiudo*/
            this.postPreviewService.closePostPreview();

            if (
                community?.type === CommunityType.PROJECT &&
                this.uiStateService.state.communitiesUI.staticData()?.[
                    hashtag.communityId
                ]?.dashboardView === DashboardView.Gantt
            ) {
                this.uiStateService.changeCommunityView(
                    hashtag.communityId,
                    DashboardView.Extended,
                );
            }

            this.store.dispatch(
                changeFilters({
                    fetchType: 'dashboard',
                    updatedFilters: {
                        ...emptyPostFilters(),
                        communityId: hashtag.communityId,
                        hashtag:
                            searchTypes.includes('post') ||
                            searchTypes.includes('both')
                                ? [hashtag]
                                : null,
                        attachmentHashtag:
                            searchTypes.includes('attachment') ||
                            searchTypes.includes('both')
                                ? [hashtag]
                                : null,
                    },
                }),
            );
            this.router.navigate([dashboardCommunity, hashtag.communityId]);
        }
        this.close();
    }
}
